import { HttpClient, HttpContext, HttpHeaders } from '@angular/common/http';
import { Injectable, signal } from '@angular/core';
import { catchError, map, Observable, of, switchMap, tap, throwError, from } from 'rxjs';
import { environment } from 'src/app/environments/environment';
import { BYPASS_AUTH } from 'src/app/core/tokens/auth.tokens';
import { LoginFirebaseService } from 'src/app/features/auth/log-in/services/login-firebase.service';
import {CtsMigrationsResponse, ValidationResultsResponse, CtsMigracionesLaboralesResponse, ApiResponse} from 'src/app/features/management/cts/interfaces/cts-upload.interface';
import {
  GenerateCtsMigrationResponse,
  ValidateCtsResponse,
  LoadCtsResponse,
  ProgressCtsResponse
} from '../interfaces/cts-upload.interface';

@Injectable({
  providedIn: 'root'
})
export class UploadCtsService {

  //private baseUrl = environment.apiUrl_personas;
  private baseUrl = environment.apiUrl;
  //private baseUrl = 'https://conectados-persona-86072408712.us-east4.run.app';

  public migrationId = signal<number | null>(null);
  private uploadUrl = signal<string | null>(null);
  public firmaAnalisis = signal<string | null>(null);
  

  constructor(
    private http: HttpClient,
    private loginFirebaseService: LoginFirebaseService
  ) {}

  /* ============================================
     MÉTODO CENTRAL PARA OBTENER HEADERS AUTH
  ============================================ */
  private withAuthHeaders(): Observable<HttpHeaders> {
    return from(this.loginFirebaseService.getNonprodToken()).pipe(
      map(token => {
        if (!token) {
          throw new Error('No hay token nonprod disponible');
        }

        return new HttpHeaders({
          'Authorization': `Bearer ${token}`,
          'X-Tenant': 'Peru'
        });
      })
    );
  }

  /* ============================================
     PASO 1 - CREAR MIGRACIÓN
  ============================================ */
  createMigration(nombre_documento: string, descripcion: string, regimen: 'comun' | 'agrario_semestral'): Observable<boolean> {

    return this.withAuthHeaders().pipe(
      switchMap(headers =>
        this.http.post<GenerateCtsMigrationResponse>(
          `${this.baseUrl}/api/v1/personas/documentos-laborales/cts/subir-archivo`,
          { nombre_documento, descripcion, regimen },
          { headers }
        )
      ),
      tap(resp => {
        this.migrationId.set(resp.migration_id);
        this.uploadUrl.set(resp.upload_url);
      }),
      map(() => true),
      catchError(error => throwError(() => error))
    );
  }

  /* ============================================
     PASO 2 - SUBIR ZIP A GCS (SIN AUTH)
  ============================================ */
  uploadZipToGCS(file: File): Observable<boolean> {

    if (!this.uploadUrl()) {
      return throwError(() => new Error('No se obtuvo la URL de carga para subir el archivo ZIP'));
    }

    const headers = new HttpHeaders({
      'Content-Type': 'application/zip'
    });

    const context = new HttpContext().set(BYPASS_AUTH, true);

    return this.http.put(this.uploadUrl()!, file, { headers, context }).pipe(
      map(() => true),
      catchError(() =>
        throwError(() => new Error('Error subiendo archivo a GCS'))
      )
    );
  }

/* ============================================
   PASO 3 - VALIDAR ZIP
============================================ */
validateZip(): Observable<ValidateCtsResponse> {

  if (!this.migrationId()) {
    return throwError(() => new Error('Migration ID no definido'));
  }

  return this.withAuthHeaders().pipe(

    switchMap(headers =>
      this.http.post<ValidateCtsResponse>(
        `${this.baseUrl}/api/v1/personas/documentos-laborales/cts/validar-zip`,
        { migration_id: this.migrationId() },
        { headers }
      )
    ),

    tap(resp => {
      this.firmaAnalisis.set(resp.firma_analisis ?? null);
    }),

    catchError(error => throwError(() => error))

  );
}
  /* ============================================
     PASO 4 - CARGAR
  ============================================ */
loadCts(): Observable<LoadCtsResponse> {

  const migrationId = this.migrationId();
  const firma = this.firmaAnalisis();

  if (!migrationId || !firma) {
    return throwError(() => new Error('Datos incompletos para cargar'));
  }

  return this.withAuthHeaders().pipe(
    switchMap(headers =>
      this.http.post<LoadCtsResponse>(
        `${this.baseUrl}/api/v1/personas/documentos-laborales/cts/cargar`,
        {
          migration_id: migrationId,
          firma: firma
        },
        { headers }
      )
    ),
    catchError(error => throwError(() => error))
  );
}

  /* ============================================
     PASO 5 - PROGRESO
  ============================================ */
getProgress(migrationId?: number): Observable<ProgressCtsResponse> {

  const id = migrationId ?? this.migrationId();

  if (!id) {
    return throwError(() => new Error('Migration ID no definido'));
  }

  return this.withAuthHeaders().pipe(
    switchMap(headers =>
      this.http.get<ProgressCtsResponse>(
        `${this.baseUrl}/api/v1/personas/documentos-laborales/cts/migracion/${id}/progreso`,
        { headers }
      )
    ),
    catchError(error => throwError(() => error))
  );
}

  /* ============================================
     FLUJO COMPLETO (1-2-3)
  ============================================ */
  uploadZipAndValidate(
    file: File,
    nombre_documento: string,
    descripcion: string,
    regimen: 'comun' | 'agrario_semestral'
  ): Observable<ValidateCtsResponse> {

    return this.createMigration(nombre_documento, descripcion, regimen).pipe(
      switchMap(() => this.uploadZipToGCS(file)),
      switchMap(() => this.validateZip()),
      catchError(error => throwError(() => error))
    );
  }


getCtsMigrations(
  offset: number = 0,
  limit: number = 10,
  filters?: {
    palabra_clave?: string | null;
    nombre_usuario_creacion?: string | null;
    dni_usuario_creacion?: string | null;
    fecha_creacion_gte?: string | null;
    fecha_creacion_lte?: string | null;
    estado?: string | null;
    regimen?: string | null;
  }
): Observable<CtsMigrationsResponse> {

  return this.withAuthHeaders().pipe(
    switchMap(headers => {

      let params: any = { offset, limit };

      const palabra = filters?.palabra_clave?.trim();

      if (palabra) {
        const esSoloNumeros = /^\d+$/.test(palabra);
        if (esSoloNumeros) {
          params.dni_usuario_creacion = palabra;
        } else {
          params.nombre_usuario_creacion = palabra;
        }
      }

      if (filters?.nombre_usuario_creacion) {
        params.nombre_usuario_creacion = filters.nombre_usuario_creacion;
      }

      if (filters?.dni_usuario_creacion) {
        params.dni_usuario_creacion = filters.dni_usuario_creacion;
      }

      if (filters?.fecha_creacion_gte) {
        params.fecha_creacion_gte = filters.fecha_creacion_gte;
      }

      if (filters?.fecha_creacion_lte) {
        params.fecha_creacion_lte = filters.fecha_creacion_lte;
      }

      if (filters?.estado) {
        params.estado = filters.estado;
      }

      if (filters?.regimen) {
        params.regimen = filters.regimen;
      }

      return this.http.get<CtsMigrationsResponse>(
        `${this.baseUrl}/api/v1/personas/documentos-laborales/cts/migraciones`,
        { headers, params }
      );
    }),
    catchError(error => throwError(() => error))
  );
}



/* ============================================
   DESCARGAR DOCUMENTO (OBTENER SIGNED URL)
============================================ */
downloadDocumento(documentoId: number): Observable<string> {

  return this.withAuthHeaders().pipe(
    switchMap(headers =>
      this.http.get<{ url: string }>(
        `${this.baseUrl}/api/v1/personas/documentos-laborales/cts/descargar/${documentoId}`,
        { headers }
      )
    ),
    map(response => response.url),
    catchError(error => {
      console.error('Error al obtener URL de descarga:', error);
      return throwError(() => new Error('No se pudo generar la descarga'));
    })
  );
}


generatePresignedUrl(fileUrl: string, expirationHours = 1): Observable<{ presigned_url: string }> {
  const cleanBaseUrl = this.baseUrl.replace(/\/$/, '');
  const url = `${cleanBaseUrl}/api/v1/permissions/permissions/generate_url_file`;

  const body = {
    file_url: fileUrl,
    expiration_hours: expirationHours
  };

  const headers = new HttpHeaders({
    'token-type': 'firebase'
  });

  return this.http.post<{ presigned_url: string }>(url, body, { headers }).pipe(
    catchError(err => {
      
      return throwError(() => err);
    })
  );
}
getCtsMigrationsLaborales(
  offset: number = 0,
  limit: number = 10,
  filters?: {
    dniColaborador?: string | null;
    nombresColaborador?: string | null;
    dniUsuarioCreacion?: string | null;
    regimen?: string | null;
    read?: boolean | null;
    download?: boolean | null;
    cargueId?: number | null;
    countRead_gte?: number | null;
    countRead_lte?: number | null;
    countDownload_gte?: number | null;
    countDownload_lte?: number | null;
    readAt_gte?: string | null;
    readAt_lte?: string | null;
    lastDownload_gte?: string | null;
    lastDownload_lte?: string | null;
    date_gte?: string | null;
    date_lte?: string | null;
    fechaCreacion_gte?: string | null;
    fechaCreacion_lte?: string | null;
  }
): Observable<CtsMigracionesLaboralesResponse> {

  return this.withAuthHeaders().pipe(
    switchMap(headers => {

      let params: any = { offset, limit };

      if (filters?.dniColaborador)     params.dniColaborador     = filters.dniColaborador;
      if (filters?.nombresColaborador) params.nombresColaborador = filters.nombresColaborador;
      if (filters?.dniUsuarioCreacion) params.dniUsuarioCreacion = filters.dniUsuarioCreacion;
      if (filters?.regimen)            params.regimen            = filters.regimen;
      if (filters?.read != null)       params.read               = filters.read;
      if (filters?.download != null)   params.download           = filters.download;
      if (filters?.cargueId != null)   params.cargueId           = filters.cargueId;

      if (filters?.countRead_gte != null)     params.countRead_gte     = filters.countRead_gte;
      if (filters?.countRead_lte != null)     params.countRead_lte     = filters.countRead_lte;
      if (filters?.countDownload_gte != null) params.countDownload_gte = filters.countDownload_gte;
      if (filters?.countDownload_lte != null) params.countDownload_lte = filters.countDownload_lte;
      if (filters?.readAt_gte)                params.readAt_gte        = filters.readAt_gte;
      if (filters?.readAt_lte)                params.readAt_lte        = filters.readAt_lte;
      if (filters?.lastDownload_gte)          params.lastDownload_gte  = filters.lastDownload_gte;
      if (filters?.lastDownload_lte)          params.lastDownload_lte  = filters.lastDownload_lte;
      if (filters?.date_gte)                  params.date_gte          = filters.date_gte;
      if (filters?.date_lte)                  params.date_lte          = filters.date_lte;
      if (filters?.fechaCreacion_gte)         params.fechaCreacion_gte = filters.fechaCreacion_gte;
      if (filters?.fechaCreacion_lte)         params.fechaCreacion_lte = filters.fechaCreacion_lte;

      return this.http.get<CtsMigracionesLaboralesResponse>(
        `${this.baseUrl}/api/v1/personas/documentos-laborales/cts/migraciones-documentos`,
        { headers, params }
      );

    }),
    catchError(error => throwError(() => error))
  );
}
exportCtsMigracionesLaborales(
  filters?: {
    offset?: number | null;
    limit?: number | null;
    dniColaborador?: string | null;
    nombresColaborador?: string | null;
    dniUsuarioCreacion?: string | null;
    regimen?: string | null;
    read?: boolean | null;
    download?: boolean | null;
    cargueId?: number | null;
    countRead?: number | null;
    countRead_gte?: number | null;
    countRead_lte?: number | null;
    countRead_gt?: number | null;
    countRead_lt?: number | null;
    countDownload?: number | null;
    countDownload_gte?: number | null;
    countDownload_lte?: number | null;
    countDownload_gt?: number | null;
    countDownload_lt?: number | null;
    readAt_gte?: string | null;
    readAt_lte?: string | null;
    readAt_gt?: string | null;
    readAt_lt?: string | null;
    firstRead_gte?: string | null;
    firstRead_lte?: string | null;
    firstRead_gt?: string | null;
    firstRead_lt?: string | null;
    lastRead_gte?: string | null;
    lastRead_lte?: string | null;
    lastRead_gt?: string | null;
    lastRead_lt?: string | null;
    firstDownload_gte?: string | null;
    firstDownload_lte?: string | null;
    firstDownload_gt?: string | null;
    firstDownload_lt?: string | null;
    lastDownload_gte?: string | null;
    lastDownload_lte?: string | null;
    lastDownload_gt?: string | null;
    lastDownload_lt?: string | null;
    date_gte?: string | null;
    date_lte?: string | null;
    date_gt?: string | null;
    date_lt?: string | null;
    fechaCreacion_gte?: string | null;
    fechaCreacion_lte?: string | null;
    fechaCreacion_gt?: string | null;
    fechaCreacion_lt?: string | null;
  }
): Observable<Blob> {

  return this.withAuthHeaders().pipe(
    switchMap(headers => {

      let params: any = {};

      if (filters?.offset != null)         params.offset             = filters.offset;
      if (filters?.limit != null)          params.limit              = filters.limit;

      if (filters?.dniColaborador)         params.dniColaborador     = filters.dniColaborador;
      if (filters?.nombresColaborador)     params.nombresColaborador = filters.nombresColaborador;
      if (filters?.dniUsuarioCreacion)     params.dniUsuarioCreacion = filters.dniUsuarioCreacion;
      if (filters?.regimen)                params.regimen            = filters.regimen;
      if (filters?.read != null)           params.read               = filters.read;
      if (filters?.download != null)       params.download           = filters.download;
      if (filters?.cargueId != null)       params.cargueId           = filters.cargueId;

      if (filters?.countRead != null)         params.countRead         = filters.countRead;
      if (filters?.countRead_gte != null)     params.countRead_gte     = filters.countRead_gte;
      if (filters?.countRead_lte != null)     params.countRead_lte     = filters.countRead_lte;
      if (filters?.countRead_gt != null)      params.countRead_gt      = filters.countRead_gt;
      if (filters?.countRead_lt != null)      params.countRead_lt      = filters.countRead_lt;

      if (filters?.countDownload != null)     params.countDownload     = filters.countDownload;
      if (filters?.countDownload_gte != null) params.countDownload_gte = filters.countDownload_gte;
      if (filters?.countDownload_lte != null) params.countDownload_lte = filters.countDownload_lte;
      if (filters?.countDownload_gt != null)  params.countDownload_gt  = filters.countDownload_gt;
      if (filters?.countDownload_lt != null)  params.countDownload_lt  = filters.countDownload_lt;

      if (filters?.readAt_gte)                params.readAt_gte        = filters.readAt_gte;
      if (filters?.readAt_lte)                params.readAt_lte        = filters.readAt_lte;
      if (filters?.readAt_gt)                 params.readAt_gt         = filters.readAt_gt;
      if (filters?.readAt_lt)                 params.readAt_lt         = filters.readAt_lt;

      if (filters?.firstRead_gte)             params.firstRead_gte     = filters.firstRead_gte;
      if (filters?.firstRead_lte)             params.firstRead_lte     = filters.firstRead_lte;
      if (filters?.firstRead_gt)              params.firstRead_gt      = filters.firstRead_gt;
      if (filters?.firstRead_lt)              params.firstRead_lt      = filters.firstRead_lt;

      if (filters?.lastRead_gte)              params.lastRead_gte      = filters.lastRead_gte;
      if (filters?.lastRead_lte)              params.lastRead_lte      = filters.lastRead_lte;
      if (filters?.lastRead_gt)               params.lastRead_gt       = filters.lastRead_gt;
      if (filters?.lastRead_lt)               params.lastRead_lt       = filters.lastRead_lt;

      if (filters?.firstDownload_gte)         params.firstDownload_gte = filters.firstDownload_gte;
      if (filters?.firstDownload_lte)         params.firstDownload_lte = filters.firstDownload_lte;
      if (filters?.firstDownload_gt)          params.firstDownload_gt  = filters.firstDownload_gt;
      if (filters?.firstDownload_lt)          params.firstDownload_lt  = filters.firstDownload_lt;

      if (filters?.lastDownload_gte)          params.lastDownload_gte  = filters.lastDownload_gte;
      if (filters?.lastDownload_lte)          params.lastDownload_lte  = filters.lastDownload_lte;
      if (filters?.lastDownload_gt)           params.lastDownload_gt   = filters.lastDownload_gt;
      if (filters?.lastDownload_lt)           params.lastDownload_lt   = filters.lastDownload_lt;

      if (filters?.date_gte)                  params.date_gte          = filters.date_gte;
      if (filters?.date_lte)                  params.date_lte          = filters.date_lte;
      if (filters?.date_gt)                   params.date_gt           = filters.date_gt;
      if (filters?.date_lt)                   params.date_lt           = filters.date_lt;

      if (filters?.fechaCreacion_gte)         params.fechaCreacion_gte = filters.fechaCreacion_gte;
      if (filters?.fechaCreacion_lte)         params.fechaCreacion_lte = filters.fechaCreacion_lte;
      if (filters?.fechaCreacion_gt)          params.fechaCreacion_gt  = filters.fechaCreacion_gt;
      if (filters?.fechaCreacion_lt)          params.fechaCreacion_lt  = filters.fechaCreacion_lt;

      return this.http.get(
        `${this.baseUrl}/api/v1/personas/documentos-laborales/cts/migraciones-documentos/export`,
        { headers, params, responseType: 'blob' }
      );

    }),
    catchError(error => throwError(() => error))
  );
}

getValidationResults(
  page: number = 1,
  pageSize: number = 50,
  soloInvalidos: boolean = false
): Observable<ValidationResultsResponse> {

  const migrationId = this.migrationId();
  const firma = this.firmaAnalisis();

  if (!migrationId || !firma) {
    return throwError(() => new Error('Datos incompletos para consultar resultados'));
  }

  return this.withAuthHeaders().pipe(

    switchMap(headers =>
      this.http.get<ApiResponse<ValidationResultsResponse>>(
        `${this.baseUrl}/api/v1/personas/documentos-laborales/cts/migracion/${migrationId}/resultados-validacion`,
        {
          headers,
          params: {
            page,
            page_size: pageSize,
            solo_invalidos: soloInvalidos,
            firma
          }
        }
      )
    ),

    map(resp => resp.data),

    catchError(error => throwError(() => error))

  );
}

}



